import React, { useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const ObjectivesTrendChart = ({ objectives = [] }) => {
  const unitOptions = useMemo(() => {
    const units = [...new Set(objectives.map((o) => o.unit).filter(Boolean))];
    return units.length > 0 ? units : ['Leads'];
  }, [objectives]);

  const [selectedUnit, setSelectedUnit] = useState(unitOptions[0] || 'Leads');

  const options = useMemo(() => {
    const filtered = objectives.filter((o) => o.unit === selectedUnit);
    if (!filtered.length) return {};

    const allDates = [];
    const objectiveLines = [];
    const realLines = [];

    filtered.forEach((obj) => {
      const start = new Date(obj.startDate);
      const end = new Date(obj.endDate);
      const today = new Date();
      const totalDays = Math.max(1, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const dailyTarget = (obj.nominalValue || 0) / totalDays;

      const dates = [];
      const targetCumulative = [];
      const current = new Date(start);
      let cumTarget = 0;

      while (current <= end && current <= today) {
        const dateStr = `${current.getMonth() + 1}/${current.getDate()}`;
        dates.push(dateStr);
        cumTarget += dailyTarget;
        targetCumulative.push(Math.round(cumTarget * 100) / 100);
        current.setDate(current.getDate() + 1);
      }

      if (dates.length > 0) {
        allDates.push(...dates);
        objectiveLines.push({
          name: `${obj.clientName} (Obj)`,
          data: targetCumulative,
          dates,
        });
        const realPerDay = dates.length > 0 ? (obj.realValue || 0) / dates.length : 0;
        const realCum = dates.map((_, i) => Math.round((i + 1) * realPerDay * 100) / 100);
        realLines.push({
          name: `${obj.clientName} (Real)`,
          data: realCum,
          dates,
        });
      }
    });

    const uniqueDates = [...new Set(allDates)];
    const series = [];
    const colors = ['#2c7be5', '#00d97e', '#f5803e', '#e63757', '#27bcfd', '#6b5eae'];

    objectiveLines.forEach((line, i) => {
      series.push({
        name: line.name,
        type: 'line',
        data: line.data,
        smooth: true,
        lineStyle: { width: 2, type: 'dashed', color: colors[i % colors.length] },
        itemStyle: { color: colors[i % colors.length] },
        symbol: 'none',
      });
    });

    realLines.forEach((line, i) => {
      series.push({
        name: line.name,
        type: 'line',
        data: line.data,
        smooth: true,
        lineStyle: { width: 3, color: colors[i % colors.length] },
        itemStyle: { color: colors[i % colors.length] },
        symbol: 'circle',
        symbolSize: 4,
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colors[i % colors.length] + '30' },
            { offset: 1, color: colors[i % colors.length] + '05' },
          ]),
        },
      });
    });

    const maxDataLen = Math.max(...objectiveLines.map((l) => l.dates.length), 1);
    const xLabels = objectiveLines[0]?.dates || uniqueDates.slice(0, maxDataLen);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255,255,255,0.95)',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: { color: '#333', fontSize: 11 },
      },
      legend: {
        data: [...objectiveLines.map((l) => l.name), ...realLines.map((l) => l.name)],
        bottom: 0,
        textStyle: { fontSize: 10 },
        type: 'scroll',
      },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: xLabels,
        axisLabel: { fontSize: 9, rotate: xLabels.length > 15 ? 45 : 0, color: '#666' },
        axisLine: { lineStyle: { color: '#e0e0e0' } },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
        axisLabel: {
          fontSize: 10,
          color: '#666',
          formatter: (v) =>
            v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v,
        },
      },
      series,
    };
  }, [objectives, selectedUnit]);

  if (!objectives.length) return null;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <FontAwesomeIcon icon="chart-line" className="me-2 text-success" />
          Tendencia Acumulada
        </h6>
        {unitOptions.length > 1 && (
          <Form.Select
            size="sm"
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
            style={{ width: 130 }}
          >
            {unitOptions.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </Form.Select>
        )}
      </Card.Header>
      <Card.Body>
        {Object.keys(options).length > 0 ? (
          <ReactEChartsCore
            echarts={echarts}
            option={options}
            style={{ height: 320 }}
            opts={{ renderer: 'canvas' }}
          />
        ) : (
          <div className="text-center py-5">
            <FontAwesomeIcon icon="chart-line" size="2x" className="text-300 mb-2" />
            <p className="text-muted mb-0">No hay datos de tendencia para {selectedUnit}</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ObjectivesTrendChart;
